// src/pages/admin/AdminAddListing.js
// Formulaire d'ajout d'annonce (admin) - NE PAS MODIFIER

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { CATEGORIES } from '../../data/categories';
import { getTypesByCategory } from '../../data/listingTypes';
import { QUARTIERS, CITIES } from '../../data/quartiers';
import { LISTING_STATUS } from '../../constants/listingStatus';
import { generateSlug } from '../../utils/seo';

const AdminAddListing = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('locations');
  const [form, setForm] = useState({
    title: '',
    type: '',
    quartier: '',
    city: 'Douala',
    price: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    capacity: '',
    floor: '',
    legalStatus: '',
    viabilise: false,
    cloture: false,
    proLabel: '',
    coveredAreas: '',
    description: '',
    descriptionEn: '',
    lat: '',
    lng: '',
    ownerName: '',
    ownerPhone: '',
    amenities: '',
    status: LISTING_STATUS.DRAFT,
  });
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setImages((prev) => [...prev, ...newImages].slice(0, 12));
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (publishNow = false) => {
    if (!form.title || !form.type || !form.quartier || !form.ownerPhone) {
      alert('Veuillez remplir les champs obligatoires (*)');
      return;
    }

    setSaving(true);
    try {
      const slug = generateSlug(form.title, form.quartier, Date.now());
      const listingData = {
        ...form,
        category,
        slug,
        status: publishNow ? LISTING_STATUS.PUBLISHED : form.status,
        priceType: CATEGORIES.find((c) => c.id === category)?.priceType,
        amenities: form.amenities ? form.amenities.split(',').map((a) => a.trim()) : [],
      };
      // TODO: adminListingsAPI.create(listingData)
      console.log('Nouvelle annonce:', listingData);
      await new Promise((resolve) => setTimeout(resolve, 800));
      navigate(ROUTES.ADMIN_LISTINGS);
    } catch (error) {
      alert('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const types = getTypesByCategory(category);
  const quartiersForCity = QUARTIERS.filter((q) => q.city === form.city);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      maxWidth: '900px',
      margin: '0 auto',
      backgroundColor: '#0A0E1A',
      paddingBottom: '80px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      position: 'sticky',
      top: 0,
      backgroundColor: '#0A0E1A',
      zIndex: 50,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#FFFFFF',
      fontSize: '20px',
      cursor: 'pointer',
    },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    form: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
    categoryTabs: {
      display: 'flex',
      gap: '8px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
    },
    catTab: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 14px',
      borderRadius: '10px',
      backgroundColor: '#162033',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#A0AEC0',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    catTabActive: {
      backgroundColor: 'rgba(255, 215, 0, 0.15)',
      border: '1px solid rgba(255, 215, 0, 0.3)',
      color: '#FFD700',
    },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    row: { display: 'flex', gap: '12px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#A0AEC0' },
    required: { color: '#F56565' },
    input: {
      padding: '12px 14px',
      backgroundColor: '#111827',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '15px',
      outline: 'none',
      width: '100%',
    },
    select: {
      padding: '12px 14px',
      backgroundColor: '#111827',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '15px',
      outline: 'none',
      width: '100%',
    },
    textarea: {
      padding: '12px 14px',
      backgroundColor: '#111827',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '15px',
      outline: 'none',
      minHeight: '90px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#FFFFFF',
      cursor: 'pointer',
    },
    imageUpload: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
    },
    uploadBtn: {
      width: '80px',
      height: '80px',
      borderRadius: '8px',
      border: '2px dashed rgba(255, 215, 0, 0.3)',
      backgroundColor: '#111827',
      color: '#FFD700',
      fontSize: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    imagePreview: {
      position: 'relative',
      width: '80px',
      height: '80px',
    },
    previewImg: {
      width: '80px',
      height: '80px',
      borderRadius: '8px',
      objectFit: 'cover',
    },
    removeImgBtn: {
      position: 'absolute',
      top: '-6px',
      right: '-6px',
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      backgroundColor: '#F56565',
      color: '#FFFFFF',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
    },
    sectionLabel: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#FFD700',
      marginTop: '8px',
    },
    bottomBar: {
      position: 'fixed',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
      maxWidth: '900px',
      display: 'flex',
      gap: '10px',
      padding: '12px 20px',
      backgroundColor: '#0A0E1A',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    draftBtn: {
      flex: 1,
      padding: '14px',
      backgroundColor: '#162033',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '10px',
      color: '#FFFFFF',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    publishBtn: {
      flex: 1,
      padding: '14px',
      backgroundColor: '#FFD700',
      border: 'none',
      borderRadius: '10px',
      color: '#0A0E1A',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
    },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Ajouter une annonce | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_LISTINGS)}>←</button>
        <div style={styles.title}>Nouvelle annonce</div>
      </div>

      <div style={styles.form}>
        {/* Catégorie */}
        <div style={styles.field}>
          <label style={styles.label}>Catégorie <span style={styles.required}>*</span></label>
          <div style={styles.categoryTabs} className="hide-scrollbar">
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                style={{
                  ...styles.catTab,
                  ...(category === cat.id ? styles.catTabActive : {}),
                }}
                onClick={() => {
                  setCategory(cat.id);
                  handleChange('type', '');
                }}
              >
                <span>{cat.icon}</span>
                {cat.id === 'immobilier' ? 'Parcelles' : cat.id}
              </div>
            ))}
          </div>
        </div>

        {/* Titre */}
        <div style={styles.field}>
          <label style={styles.label}>Titre <span style={styles.required}>*</span></label>
          <input
            type="text"
            style={styles.input}
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Ex: Appartement moderne meublé 2 chambres"
          />
        </div>

        {/* Type */}
        <div style={styles.field}>
          <label style={styles.label}>Type <span style={styles.required}>*</span></label>
          <select
            style={styles.select}
            value={form.type}
            onChange={(e) => handleChange('type', e.target.value)}
          >
            <option value="">Choisir un type</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Ville + Quartier */}
        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Ville <span style={styles.required}>*</span></label>
            <select
              style={styles.select}
              value={form.city}
              onChange={(e) => {
                handleChange('city', e.target.value);
                handleChange('quartier', '');
              }}
            >
              {CITIES.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Quartier <span style={styles.required}>*</span></label>
            <select
              style={styles.select}
              value={form.quartier}
              onChange={(e) => handleChange('quartier', e.target.value)}
            >
              <option value="">Choisir</option>
              {quartiersForCity.map((q) => (
                <option key={q.id} value={q.name}>{q.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Prix */}
        <div style={styles.field}>
          <label style={styles.label}>
            Prix (FCFA)
            {category === 'sejours' && ' / nuit'}
            {category === 'locations' && ' / mois'}
            {category === 'immobilier' && ' / m²'}
          </label>
          <input
            type="number"
            style={styles.input}
            value={form.price}
            onChange={(e) => handleChange('price', e.target.value)}
            placeholder="Ex: 250000"
          />
        </div>

        {/* Champs spécifiques Séjours / Locations */}
        {(category === 'sejours' || category === 'locations') && (
          <>
            <div style={styles.sectionLabel}>Détails du logement</div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Chambres</label>
                <input type="number" style={styles.input} value={form.bedrooms} onChange={(e) => handleChange('bedrooms', e.target.value)} placeholder="2" />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Salles de bain</label>
                <input type="number" style={styles.input} value={form.bathrooms} onChange={(e) => handleChange('bathrooms', e.target.value)} placeholder="1" />
              </div>
            </div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Superficie (m²)</label>
                <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="85" />
              </div>
              {category === 'sejours' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Capacité (pers.)</label>
                  <input type="number" style={styles.input} value={form.capacity} onChange={(e) => handleChange('capacity', e.target.value)} placeholder="4" />
                </div>
              )}
              {category === 'locations' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Étage</label>
                  <input type="text" style={styles.input} value={form.floor} onChange={(e) => handleChange('floor', e.target.value)} placeholder="RDC, 1er..." />
                </div>
              )}
            </div>
          </>
        )}

        {/* Champs spécifiques Terrain/Immobilier */}
        {category === 'immobilier' && (
          <>
            <div style={styles.sectionLabel}>Détails de la parcelle</div>
            <div style={styles.field}>
              <label style={styles.label}>Superficie (m²) <span style={styles.required}>*</span></label>
              <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="500" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Statut juridique</label>
              <input type="text" style={styles.input} value={form.legalStatus} onChange={(e) => handleChange('legalStatus', e.target.value)} placeholder="Titre foncier, ..." />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Label professionnel</label>
              <input type="text" style={styles.input} value={form.proLabel} onChange={(e) => handleChange('proLabel', e.target.value)} placeholder="Parcelle viabilisée" />
            </div>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.viabilise} onChange={(e) => handleChange('viabilise', e.target.checked)} />
              Viabilisé
            </label>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.cloture} onChange={(e) => handleChange('cloture', e.target.checked)} />
              Clôturé
            </label>
          </>
        )}

        {/* Champs Services */}
        {category === 'services' && (
          <div style={styles.field}>
            <label style={styles.label}>Quartiers couverts</label>
            <input type="text" style={styles.input} value={form.coveredAreas} onChange={(e) => handleChange('coveredAreas', e.target.value)} placeholder="Bonapriso, Akwa, Bonanjo..." />
          </div>
        )}

        {/* Équipements */}
        {category !== 'immobilier' && category !== 'services' && (
          <div style={styles.field}>
            <label style={styles.label}>Équipements (séparés par virgule)</label>
            <input type="text" style={styles.input} value={form.amenities} onChange={(e) => handleChange('amenities', e.target.value)} placeholder="Wifi, Climatisation, Parking, Sécurité" />
          </div>
        )}

        {/* Photos */}
        <div style={styles.sectionLabel}>Photos</div>
        <div style={styles.imageUpload}>
          {images.map((img, index) => (
            <div key={index} style={styles.imagePreview}>
              <img src={img.preview} alt={`upload ${index}`} style={styles.previewImg} />
              <button style={styles.removeImgBtn} onClick={() => removeImage(index)}>✕</button>
            </div>
          ))}
          {images.length < 12 && (
            <label style={styles.uploadBtn}>
              +
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          )}
        </div>

        {/* Descriptions */}
        <div style={styles.sectionLabel}>Descriptions</div>
        <div style={styles.field}>
          <label style={styles.label}>Description (Français)</label>
          <textarea style={styles.textarea} value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Décrivez le bien..." />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Description (English)</label>
          <textarea style={styles.textarea} value={form.descriptionEn} onChange={(e) => handleChange('descriptionEn', e.target.value)} placeholder="Describe the property..." />
        </div>

        {/* GPS */}
        <div style={styles.sectionLabel}>Localisation GPS (optionnel)</div>
        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Latitude</label>
            <input type="text" style={styles.input} value={form.lat} onChange={(e) => handleChange('lat', e.target.value)} placeholder="4.0511" />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Longitude</label>
            <input type="text" style={styles.input} value={form.lng} onChange={(e) => handleChange('lng', e.target.value)} placeholder="9.7679" />
          </div>
        </div>

        {/* Contact propriétaire */}
        <div style={styles.sectionLabel}>Contact propriétaire</div>
        <div style={styles.field}>
          <label style={styles.label}>Nom du propriétaire</label>
          <input type="text" style={styles.input} value={form.ownerName} onChange={(e) => handleChange('ownerName', e.target.value)} placeholder="Ex: M. Kamga" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>WhatsApp / Téléphone <span style={styles.required}>*</span></label>
          <input type="tel" style={styles.input} value={form.ownerPhone} onChange={(e) => handleChange('ownerPhone', e.target.value)} placeholder="+237 6XX XXX XXX" />
        </div>
      </div>

      <div style={styles.bottomBar}>
        <button
          style={{ ...styles.draftBtn, opacity: saving ? 0.6 : 1 }}
          onClick={() => handleSubmit(false)}
          disabled={saving}
        >
          Enregistrer brouillon
        </button>
        <button
          style={{ ...styles.publishBtn, opacity: saving ? 0.6 : 1 }}
          onClick={() => handleSubmit(true)}
          disabled={saving}
        >
          {saving ? 'Enregistrement...' : 'Publier'}
        </button>
      </div>
    </div>
  );
};

export default AdminAddListing;